import { client, gql } from 'data-provider';
import { EStatus } from 'enums';
import { IPayment, ISale, ISaleStockCorrection } from 'interfaces';
import { EDiscountEntryType } from 'utilities/discount-entry';
import type { CashMemoSale } from 'utilities/cash-memo';

export enum EInvoiceType {
  Sales = 'Sales',
  Salesman = 'Salesman',
}

export interface SalesQuery {
  startDate?: string | null;
  endDate?: string | null;
  searchString?: string;
  statuses?: EStatus[];
  warehouse?: string | null;
  start?: number;
  limit?: number;
  user?: string | null;
}

export interface CreateSaleData {
  client: string | null;
  newClientDetails: {
    name: string;
    mobileNumber: string;
    countryCode: string;
  } | null;
  clientPoc?: string;
  salesDate: Date;
  salesStatus: string;
  warehouse: string;
  salesAddress: string;
  note?: string;
  shipmentCost?: number;
  vatAmount?: number;
  discountCost?: number;
  discountEntryValue?: number | null;
  discountEntryType?: EDiscountEntryType | null;
  salesExpense?: { id: string }[];
  salesPayments?: IPayment[];
  salesChannel?: string;
  // Aziz Auto A4: the invoice header chosen on the sale; empty stores the default.
  invoiceProfileId?: string;
  salesProducts: {
    sku: string;
    quantity: number;
    unitPrice: number;
    discountCost?: number;
    discountEntryValue?: number | null;
    discountEntryType?: EDiscountEntryType | null;
  }[];
  transactionToken: string;
}

type CreateSalesStockCorrectionData = Omit<
  CreateSaleData,
  | 'salesProducts'
  | 'salesExpense'
  | 'salesPayments'
  | 'newClientDetails'
  // stock_correction_sales stores taka `discountCost` only.
  | 'discountEntryValue'
  | 'discountEntryType'
> & {
  stockCorrectionSalesProducts: {
    sku: string;
    unitPrice: number;
    quantity: number;
    productVariantId: string;
  }[];
};

export const getSales = async (saleQuery?: SalesQuery) => {
  const where = {
    query: saleQuery && saleQuery.searchString ? saleQuery.searchString : '',
    startDate: saleQuery && saleQuery.startDate ? saleQuery.startDate : undefined,
    endDate: saleQuery && saleQuery.endDate ? saleQuery.endDate : undefined,
    statuses: saleQuery && saleQuery.statuses ? saleQuery.statuses : undefined,
    warehouse: saleQuery && saleQuery.warehouse ? saleQuery.warehouse : undefined,
    user: saleQuery && saleQuery.user ? saleQuery.user : undefined,
  };

  const query = gql`
    query ($sort: SortInput, $where: JSON, $start: Int, $limit: Int) {
      salesCopy(sort: $sort, where: $where, start: $start, limit: $limit) {
        data {
          id
          orderType
          salesDate
          salesStatus
          totalAmount
          salesCompletedAt
          client {
            name
            clientCode
            mobileNumber
          }
          createdBy {
            name
            email
            id
            mobileNumber
          }
          warehouse {
            name
            id
          }
          salesProductsCopy {
            id
            quantity
            unitPrice
            subTotal
            productVariant {
              id
              name
              sku
            }
          }
        }
        page
        size
        total
      }
    }
  `;

  const variables = {
    where,
    start: saleQuery && saleQuery.start ? saleQuery.start : 1,
    limit: saleQuery && saleQuery.limit ? saleQuery.limit : 10,
  };
  const data = await client.request(query, variables);
  return data.salesCopy as {
    data: ISale[];
    page: number;
    size: number;
    total: number;
  };
};

export const getSale = async (saleId: string) => {
  const query = gql`
    query ($saleCopyId: ID!) {
      saleCopy(id: $saleCopyId) {
        id
        salesAddress
        note
        salesStatus
        vatAmount
        shipmentCost
        discountCost
        discountEntryValue
        discountEntryType
        subTotal
        totalAmount
        salesDate
        client {
          id
          name
          clientCode
          mobileNumber
        }
        clientPoc {
          id
          name
          email
          designation
          mobileNumber
        }
        warehouse {
          id
          name
        }
        salesProductsCopy {
          id
          quantity
          unitPrice
          subTotal
          discountCost
          discountEntryValue
          discountEntryType
          productVariant {
            id
            name
            sku
            photo {
              url
            }
            productCopy {
              id
              name
              sku
            }
          }
        }
      }
    }
  `;

  const variables = {
    saleCopyId: saleId,
  };

  const data = await client.request(query, variables);
  return data.saleCopy as ISale;
};

// A sale with what the cash memo prints: channel, lines, VAT and payments (Stitches S7).
export const getCashMemoSale = async (saleId: string) => {
  const query = gql`
    query ($saleCopyId: ID!) {
      saleCopy(id: $saleCopyId) {
        id
        salesChannel
        salesDate
        subTotal
        vatAmount
        discountCost
        discountEntryValue
        discountEntryType
        shipmentCost
        totalAmount
        createdBy {
          name
        }
        salesPaymentLogsCopy {
          currentPaidAmount
        }
        salesProductsCopy {
          quantity
          unitPrice
          subTotal
          discountCost
          discountEntryValue
          discountEntryType
          productVariant {
            name
            sku
          }
        }
      }
    }
  `;
  const data = await client.request(query, { saleCopyId: saleId });
  const sale = data.saleCopy as Omit<CashMemoSale, 'paidAmount' | 'processedBy'> & {
    salesChannel: string | null;
    createdBy: { name: string } | null;
    salesPaymentLogsCopy: { currentPaidAmount: number }[];
  };
  return {
    salesChannel: sale.salesChannel,
    cashMemoSale: {
      ...sale,
      paidAmount: sale.salesPaymentLogsCopy.reduce(
        (total, payment) => total + (payment.currentPaidAmount || 0),
        0,
      ),
      processedBy: sale.createdBy?.name ?? null,
    } as CashMemoSale,
  };
};

export const createSale = async (data: CreateSaleData) => {
  const createSale = gql`
    mutation ($input: createSalesCopyInput!) {
      createSalesCopy(input: $input) {
        id
      }
    }
  `;

  const variables = {
    input: { data },
  };

  const createdData = await client.request(createSale, variables);
  return createdData.createSalesCopy as { id: string };
};

export const updateSale = async (id: string, salesStatus: string) => {
  const updateSaleStatus = gql`
    mutation ($input: updateSaleInput) {
      updateSale(input: $input) {
        id
      }
    }
  `;

  const variables = {
    input: {
      where: { id },
      data: { salesStatus },
    },
  };
  const updatedData = await client.request(updateSaleStatus, variables);
  return updatedData;
};

export const deleteSale = async (id: string) => {
  const deleteSale = gql`
    mutation ($input: deleteSalesCopyInput!) {
      deleteSaleCopy(input: $input) {
        saleCopy {
          id
        }
      }
    }
  `;

  const variables = {
    input: {
      where: { id },
    },
  };

  await client.request(deleteSale, variables);
};

export const getSaleInvoice = async (id: string, invoiceType: EInvoiceType) => {
  const query = gql`
    query Query(
      $generateSaleInvoicePdfId: ID!
      $invoiceType: AllowedSalesInvoiceEnum!
      $timeZone: String!
    ) {
      generateSaleInvoicePdf(
        id: $generateSaleInvoicePdfId
        invoiceType: $invoiceType
        timeZone: $timeZone
      )
    }
  `;

  const variables = {
    generateSaleInvoicePdfId: id,
    invoiceType,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
  const data = await client.request(query, variables);
  return data.generateSaleInvoicePdf as string;
};

export const getSalesDropdown = async (startDate?: string, endDate?: string) => {
  const where = { startDate, endDate };

  const query = gql`
    query ($where: JSON) {
      salesCopyDropdown(where: $where) {
        id
        salesDate
        salesStatus
        totalAmount
        client {
          name
          mobileNumber
        }
      }
    }
  `;

  const variables = { where };

  const data = await client.request(query, variables);
  return data.salesCopyDropdown as ISale[];
};

export const updateSaleStatus = async (id: string, salesStatus: string) => {
  const mutation = gql`
    mutation ($input: updateSaleCopyStatusInput!) {
      updateSaleCopyStatus(input: $input) {
        saleCopy {
          id
        }
      }
    }
  `;

  const variables = {
    input: {
      where: { id },
      data: { salesStatus },
    },
  };
  const updatedData = await client.request(mutation, variables);
  return updatedData;
};

export interface KitchenProduct {
  id: string;
  productVariant: {
    id: string;
    name: string;
    photo?: {
      url: string;
    };
    sku: string;
    productTags: {
      tag: {
        name: string;
      };
    }[];
  };
  quantity: number;
  salesProductStatus: string;
  subTotal: number;
  unitPrice: number;
  salesCopy: {
    id: string;
    warehouse: {
      name: string;
    };
    orderType: string;
    salesCompletedAt: string;
    salesDate: string;
    salesStatus: string;
  };
}

export const getKitchenSales = async (
  saleQuery?: Omit<SalesQuery, 'warehouse'> & { tag?: string | null },
) => {
  const where = {
    query: saleQuery && saleQuery.searchString ? saleQuery.searchString : '',
    startDate: saleQuery && saleQuery.startDate ? saleQuery.startDate : undefined,
    endDate: saleQuery && saleQuery.endDate ? saleQuery.endDate : undefined,
    statuses: saleQuery && saleQuery.statuses ? saleQuery.statuses : undefined,
    tag: saleQuery && saleQuery.tag ? saleQuery.tag : undefined,
  };

  const query = gql`
    query ($where: JSON, $start: Int, $limit: Int, $sort: SortInput) {
      kitchenProducts(where: $where, start: $start, limit: $limit, sort: $sort) {
        data {
          id
          productVariant {
            id
            name
            sku
            photo {
              url
            }
            productTags {
              tag {
                name
              }
            }
          }
          quantity
          salesProductStatus
          subTotal
          unitPrice
          salesCopy {
            id
            warehouse {
              name
            }
            orderType
            salesCompletedAt
            salesDate
            salesStatus
          }
        }
        total
        size
        page
      }
    }
  `;

  const variables = {
    where,
    start: saleQuery && saleQuery.start ? saleQuery.start : 1,
    limit: saleQuery && saleQuery.limit ? saleQuery.limit : 10,
  };
  const data = await client.request(query, variables);
  return data.kitchenProducts as {
    data: KitchenProduct[];
    page: number;
    size: number;
    total: number;
  };
};

export const updateSaleProductStatus = async (id: string, productStatus: string) => {
  const mutation = gql`
    mutation UpdateKitchenStatus($input: salesProductStatusUpdateDto!) {
      updateKitchenStatus(input: $input) {
        id
      }
    }
  `;

  const variables = {
    input: {
      salesProductId: id,
      salesProductStatus: productStatus,
    },
  };
  const updatedData = await client.request(mutation, variables);
  return updatedData;
};

export const deleteRestaurantSale = async (id: string) => {
  const deleteSale = gql`
    mutation DeleteRestaurantPos($input: deleteSalesCopyInput!) {
      deleteRestaurantPos(input: $input) {
        saleCopy {
          id
        }
      }
    }
  `;

  const variables = {
    input: {
      where: { id },
    },
  };

  await client.request(deleteSale, variables);
};

export const updateRestaurantPOSSaleStatus = async (id: string, status: string) => {
  const mutation = gql`
    mutation ($input: salesStatusUpdateDto!) {
      updateRestaurantPOSStatus(input: $input) {
        id
      }
    }
  `;

  const variables = {
    input: {
      salesId: id,
      salesStatus: status,
    },
  };
  const updatedData = await client.request(mutation, variables);
  return updatedData;
};

export type SalesAndProductStatusCountResponse = {
  salesProductStatusCount: {
    completed: number;
    orderConfirmed: number;
    deliveryInProgress: number;
  };
  salesStatusCount: {
    completed: number;
    orderConfirmed: number;
    deliveryInProgress: number;
  };
};

type SalesStatusCountQuery = Omit<
  SalesQuery,
  'searchString' | 'statuses' | 'start' | 'limit' | 'user'
> & {
  tag?: string | null;
  userId?: string | null;
};

export const getSalesAndProductStatusCount = async (saleQuery?: SalesStatusCountQuery) => {
  const where: SalesStatusCountQuery = {
    startDate: saleQuery && saleQuery.startDate ? saleQuery.startDate : undefined,
    endDate: saleQuery && saleQuery.endDate ? saleQuery.endDate : undefined,
    warehouse: saleQuery && saleQuery.warehouse ? saleQuery.warehouse : undefined,
    userId: saleQuery && saleQuery.userId ? saleQuery.userId : undefined,
    tag: saleQuery && saleQuery.tag ? saleQuery.tag : undefined,
  };

  const query = gql`
    query ($where: JSON) {
      orderAndSalesProductCount(where: $where) {
        salesProductStatusCount {
          completed
          orderConfirmed
          deliveryInProgress
        }
        salesStatusCount {
          completed
          orderConfirmed
          deliveryInProgress
        }
      }
    }
  `;
  const variables = {
    where,
  };

  const data = await client.request(query, variables);

  return data.orderAndSalesProductCount as SalesAndProductStatusCountResponse;
};

export const getRestaurantPosInvoice = async (
  clientId: string,
  startDate: string,
  endDate: string,
) => {
  const query = gql`
    query Query(
      $clientId: String!
      $startDate: DateTime!
      $endDate: DateTime!
      $timeZone: String!
    ) {
      getTodaySalesOfAClient(
        clientId: $clientId
        startDate: $startDate
        endDate: $endDate
        timeZone: $timeZone
      )
    }
  `;

  const variables = {
    clientId,
    startDate,
    endDate,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
  const data = await client.request(query, variables);
  return data.getTodaySalesOfAClient as string;
};

export const getSalesStockCorrections = async (saleQuery?: SalesQuery) => {
  const where = {
    query: saleQuery && saleQuery.searchString ? saleQuery.searchString : '',
    startDate: saleQuery && saleQuery.startDate ? saleQuery.startDate : undefined,
    endDate: saleQuery && saleQuery.endDate ? saleQuery.endDate : undefined,
    statuses: saleQuery && saleQuery.statuses ? saleQuery.statuses : undefined,
    warehouse: saleQuery && saleQuery.warehouse ? saleQuery.warehouse : undefined,
    user: saleQuery && saleQuery.user ? saleQuery.user : undefined,
  };

  const query = gql`
    query ($sort: SortInput, $where: JSON, $start: Int, $limit: Int) {
      stockCorrectionSales(sort: $sort, where: $where, start: $start, limit: $limit) {
        data {
          id
          orderType
          salesDate
          salesStatus
          totalAmount
          salesCompletedAt
          client {
            name
            clientCode
            mobileNumber
          }
          createdBy {
            name
            email
            id
            mobileNumber
          }
          warehouse {
            name
            id
          }
          stockCorrectionSalesProducts {
            id
            quantity
            unitPrice
            subTotal
            productVariant {
              id
              name
              sku
            }
          }
        }
        page
        size
        total
      }
    }
  `;

  const variables = {
    where,
    start: saleQuery && saleQuery.start ? saleQuery.start : 1,
    limit: saleQuery && saleQuery.limit ? saleQuery.limit : 10,
  };
  const data = await client.request(query, variables);
  return data.stockCorrectionSales as {
    data: ISaleStockCorrection[];
    page: number;
    size: number;
    total: number;
  };
};

export const deleteSalesStockCorrection = async (id: string) => {
  const deleteSale = gql`
    mutation ($input: deleteStockCorrectionSalesInput!) {
      deleteStockCorrectionSales(input: $input) {
        stockCorrectionSales {
          id
        }
      }
    }
  `;

  const variables = {
    input: {
      where: { id },
    },
  };

  await client.request(deleteSale, variables);
};

export const updateSalesStockCorrectionStatus = async (id: string, salesStatus: string) => {
  const mutation = gql`
    mutation ($input: updateStockCorrectionSalesStatusInput!) {
      updateStockCorrectionSalesStatus(input: $input) {
        stockCorrectionSales {
          id
        }
      }
    }
  `;

  const variables = {
    input: {
      where: { id },
      data: { salesStatus },
    },
  };
  const updatedData = await client.request(mutation, variables);
  return updatedData;
};

export const createSalesStockCorrection = async (data: CreateSalesStockCorrectionData) => {
  const createSale = gql`
    mutation ($input: createStockCorrectionSalesInput!) {
      createStockCorrectionSales(input: $input) {
        id
      }
    }
  `;

  const variables = {
    input: { data },
  };

  const createdData = await client.request(createSale, variables);
  return createdData.createStockCorrectionSales as { id: string };
};

export const getSalesStockCorrection = async (saleId: string) => {
  const query = gql`
    query ($stockCorrectionSaleId: ID!) {
      stockCorrectionSale(id: $stockCorrectionSaleId) {
        id
        salesAddress
        note
        salesStatus
        vatAmount
        shipmentCost
        discountCost
        subTotal
        totalAmount
        salesDate
        client {
          id
          name
          clientCode
          mobileNumber
        }
        clientPoc {
          id
          name
          email
          designation
          mobileNumber
        }
        warehouse {
          id
          name
        }
        stockCorrectionSalesProducts {
          id
          quantity
          unitPrice
          subTotal
          productVariant {
            id
            name
            sku
            photo {
              url
            }
            productCopy {
              id
              name
              sku
            }
          }
        }
      }
    }
  `;

  const variables = {
    stockCorrectionSaleId: saleId,
  };

  const data = await client.request(query, variables);
  return data.stockCorrectionSale as ISaleStockCorrection;
};
